/**
 * LoanBalanceSlider — Dual sliders for income vs debt.
 * Use case: Loan/Real Estate — Financial balancing with DTI ratio display.
 */
import React from 'react';
import { Box, Slider, Typography } from '@mui/material';

export default function LoanBalanceSlider({
  income: incomeDefault = 5000,
  debt: debtDefault = 1000,
  value,
  onChange,
  incomeMin = 0,
  incomeMax = 20000,
  debtMin = 0,
  debtMax = 10000,
  step = 100,
  label = 'Income vs debt',
  ...props
}) {
  const incomeVal = Number(value?.income ?? incomeDefault) || 0;
  const debtVal = Number(value?.debt ?? debtDefault) || 0;
  const dti = incomeVal > 0 ? Math.round((debtVal / incomeVal) * 100) : 0;

  const handleIncomeChange = (_, v) => {
    onChange?.({ target: { value: { income: v, debt: debtVal } } });
  };
  const handleDebtChange = (_, v) => {
    onChange?.({ target: { value: { income: incomeVal, debt: v } } });
  };

  return (
    <Box sx={{ px: 2, py: 1 }} {...props}>
      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Monthly income
      </Typography>
      <Slider
        value={incomeVal}
        onChange={handleIncomeChange}
        min={incomeMin}
        max={incomeMax}
        step={step}
        valueLabelDisplay="auto"
        valueLabelFormat={(v) => `$${v.toLocaleString()}`}
        sx={{ mb: 2 }}
      />
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Monthly debt payments
      </Typography>
      <Slider
        value={debtVal}
        onChange={handleDebtChange}
        min={debtMin}
        max={debtMax}
        step={step}
        valueLabelDisplay="auto"
        valueLabelFormat={(v) => `$${v.toLocaleString()}`}
        color="secondary"
        sx={{ mb: 2 }}
      />
      <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Debt-to-income ratio
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 700, color: dti > 43 ? 'error.main' : 'success.main' }}>
          {dti}%
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {dti <= 28 ? 'Ideal' : dti <= 36 ? 'Acceptable' : dti <= 43 ? 'Stretched' : 'High risk'}
        </Typography>
      </Box>
    </Box>
  );
}
